import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useHistory } from 'react-router-dom';
import { toJS } from 'mobx';
import DynamicFormFromJSON from 'components/common/form/DynamicFormFromJSON';
import { PRODUCT_TEMPLATE_CARD_DATA } from 'global/checklists-helpers/ProductTemplateChecklistHelper';
import { CompletionStatus, ProductTemplateChecklistCard, ProductTemplateStatus } from 'types/productServiceV2';
import ActionModal from 'components/common/modals/ActionModal';
import RFQChecklistOptionHeader from 'components/common/headers/RFQChecklistOptionHeader';
import useRfqTemplate from 'global/hooks/useRfqTemplate';
import { useRFQPolicyAttributesFormStore } from 'store/pages/rfq/rfq-template/rfq-policy-attributes/pageStore';
import { CHECKLIST_HEADER_TEST_ID } from 'components/common/rfq/constants';
import {
  POLICY_ATTRIBUTES_EDITABLE_FORM_TEST_ID,
  POLICY_ATTRIBUTES_NOT_EDITABLE_FORM_TEST_ID,
  POLICY_ATTRIBUTES_WARNING_MODAL_TEST_ID,
} from '../constants';

const RFQPolicyAttributesForm = observer((): JSX.Element => {
  // Common data
  const history = useHistory();
  const { templateId, pageType } = useRfqTemplate();

  // Mobx store
  const {
    isLoading,
    form,
    dynamicForm,
    productTemplate,
    isWarningModalOpen,
    getInitialData,
    saveAnswers,
    resetInitialData,
    setWarningModalStatus,
  } = useRFQPolicyAttributesFormStore();
  const convertedTemplate = toJS(productTemplate);

  const isFormCompleted =
    convertedTemplate?.checklist.checklistOptions.find(
      (option) => option.card === ProductTemplateChecklistCard.POLICY_ATTRIBUTES,
    )?.completionStatus === CompletionStatus.COMPLETED;

  const isTemplateInProgress =
    convertedTemplate?.status === ProductTemplateStatus.IN_PROGRESS ||
    convertedTemplate?.status === ProductTemplateStatus.INITIAL_SEED;

  useEffect(() => {
    void getInitialData(templateId);
    return resetInitialData;
  }, []);

  const returnBack = (): void => {
    history.push(`/product-template-checklist/${templateId}?type=${pageType}`);
  };

  const onSaveAndExit = async (): Promise<void> => {
    await saveAnswers(false);
    returnBack();
  };

  const onCompleteForm = async (): Promise<void> => {
    if (dynamicForm?.isFormValid()) {
      await saveAnswers(true);
      returnBack();
    }
  };

  const openWarningModal = (): void => {
    setWarningModalStatus(true);
  };

  const closeWarningModal = (): void => {
    setWarningModalStatus(false);
  };

  return (
    <>
      <RFQChecklistOptionHeader
        title={PRODUCT_TEMPLATE_CARD_DATA.POLICY_ATTRIBUTES.title}
        subtitle={PRODUCT_TEMPLATE_CARD_DATA.POLICY_ATTRIBUTES.text}
        isFormCompleted={isFormCompleted}
        showWarningModals={isTemplateInProgress}
        onSaveChangesAction={isTemplateInProgress ? onSaveAndExit : undefined}
        data-test-id={CHECKLIST_HEADER_TEST_ID}
      />
      {isTemplateInProgress ? (
        <DynamicFormFromJSON
          dynamicForm={dynamicForm}
          form={form}
          loading={isLoading}
          backBtnText={isFormCompleted ? 'Back' : 'Save & Exit'}
          completeBtnText={isFormCompleted ? 'Update' : 'Complete'}
          isFormCompleted={isFormCompleted}
          onCompleteForm={isFormCompleted ? openWarningModal : onCompleteForm}
          onSaveAndExit={onSaveAndExit}
          data-test-id={POLICY_ATTRIBUTES_EDITABLE_FORM_TEST_ID}
        />
      ) : (
        <DynamicFormFromJSON
          dynamicForm={dynamicForm}
          form={form}
          isEditable={false}
          loading={isLoading}
          backBtnText="Back"
          onBack={returnBack}
          data-test-id={POLICY_ATTRIBUTES_NOT_EDITABLE_FORM_TEST_ID}
        />
      )}
      <ActionModal
        show={isWarningModalOpen}
        title="Progress Reset Confirmation"
        subtitle="Are you sure you want to proceed? This action will reset all your current progress and clear any saved data. Please confirm your decision before proceeding."
        actionButtonText="Continue"
        closeButtonText="Cancel"
        onClose={closeWarningModal}
        onAction={onCompleteForm}
        data-test-id={POLICY_ATTRIBUTES_WARNING_MODAL_TEST_ID}
      />
    </>
  );
});

export default RFQPolicyAttributesForm;
